"use client";

import { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { Link as RouterLink, useParams } from "react-router-dom";
import { getProposal, isKnownProposalSlug } from "../index";
import { jetsProposal } from "../data/jets";
import { aprilProposal } from "../data/april";
import { northProposal } from "../data/north";
import {
  hasStoredProposalAccess,
  usesProposalSiteAccess,
} from "../proposalAccess";
import { ProposalHelmet } from "../ProposalHelmet";
import ProposalPassphraseGate from "../components/ProposalPassphraseGate";
import ProposalExperience from "../components/ProposalExperience";
import JetsProposalPage from "../components/JetsProposalPage";
import AprilProposalPage from "../components/AprilProposalPage";
import NorthProposalPage from "../components/NorthProposalPage";
import { proposalFont, proposalTokens } from "../proposalTheme";
import type { ClientProposal } from "../proposalTypes";

function customProposal(slug: string) {
  if (slug === jetsProposal.slug) return jetsProposal;
  if (slug === aprilProposal.slug) return aprilProposal;
  if (slug === northProposal.slug) return northProposal;
  return null;
}

type GateTarget = {
  slug: string;
  clientName: string;
  accessEnvKey: string;
};

type LoadState =
  | { status: "booting" }
  | { status: "passphrase"; gate: GateTarget }
  | { status: "ready"; slug: string; proposal: ClientProposal | null }
  | { status: "missing" };

export default function ProposalSlugPage() {
  const { slug } = useParams<{ slug: string }>();
  const [state, setState] = useState<LoadState>({ status: "booting" });

  useEffect(() => {
    if (!slug || !isKnownProposalSlug(slug)) {
      setState({ status: "missing" });
      return;
    }
    const custom = customProposal(slug);
    const proposal = custom ? null : getProposal(slug);
    const gate: GateTarget | null = custom
      ? {
          slug: custom.slug,
          clientName: custom.clientName,
          accessEnvKey: custom.accessEnvKey,
        }
      : proposal
        ? {
            slug: proposal.slug,
            clientName: proposal.clientName,
            accessEnvKey: proposal.accessEnvKey,
          }
        : null;
    if (!gate) {
      setState({ status: "missing" });
      return;
    }
    if (usesProposalSiteAccess(gate.accessEnvKey)) {
      if (hasStoredProposalAccess(slug)) {
        setState({ status: "ready", slug, proposal });
        return;
      }
      setState({ status: "passphrase", gate });
      return;
    }
    // No access code configured: show in local/dev so the page is reviewable.
    if (import.meta.env.DEV) {
      setState({ status: "ready", slug, proposal });
      return;
    }
    setState({ status: "passphrase", gate });
  }, [slug]);

  if (state.status === "booting") {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: proposalTokens.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <CircularProgress sx={{ color: proposalTokens.accent }} />
      </Box>
    );
  }

  if (state.status === "missing") {
    return (
      <Box
        component="main"
        sx={{
          minHeight: "100vh",
          bgcolor: proposalTokens.bg,
          color: proposalTokens.text,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          px: 3,
          textAlign: "center",
          gap: 2,
        }}
      >
        <Helmet>
          <title>Proposal not found | ComedyUO</title>
          <meta name="robots" content="noindex, nofollow" />
        </Helmet>
        <Typography sx={{ fontFamily: proposalFont, fontSize: "1.25rem" }}>
          This proposal could not be found.
        </Typography>
        <Typography
          component={RouterLink}
          to="/"
          sx={{
            fontFamily: proposalFont,
            color: proposalTokens.accent,
            textDecoration: "none",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          Back to ComedyUO
        </Typography>
      </Box>
    );
  }

  if (state.status === "passphrase") {
    return (
      <ProposalPassphraseGate
        slug={state.gate.slug}
        clientName={state.gate.clientName}
        accessEnvKey={state.gate.accessEnvKey}
        jetsTheme={state.gate.slug === jetsProposal.slug}
        onVerified={() =>
          setState({
            status: "ready",
            slug: state.gate.slug,
            proposal: customProposal(state.gate.slug) ? null : getProposal(state.gate.slug),
          })
        }
      />
    );
  }

  if (state.slug === aprilProposal.slug) {
    return <AprilProposalPage />;
  }

  if (state.slug === northProposal.slug) {
    return <NorthProposalPage />;
  }

  if (state.slug === jetsProposal.slug || !state.proposal) {
    return <JetsProposalPage />;
  }

  return (
    <>
      <ProposalHelmet proposal={state.proposal} />
      <ProposalExperience proposal={state.proposal} />
    </>
  );
}
